import { forwardRef, useId } from "react";
import {
  Select as SelectPrimitive,
  type SelectProps as SelectPrimitiveProps,
  ListBoxSection,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./SelectV2.styled";
import type { SelectV2Item } from "./SelectV2.types";
import { OuterField } from "./OuterField";
import { SelectValue } from "./SelectValue";
import { ChevronIcon } from "./ChevronIcon";
import { Popover } from "./Popover";
import { ListBoxItem } from "./ListBoxItem";
import { ReactAriaProviders } from "../ReactAriaProviders";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";

export type SelectV2Props = PropsWithOptionalTestID &
  Omit<SelectPrimitiveProps, "children"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
    items: SelectV2Item[];
  };

const SelectV2 = forwardRef<HTMLButtonElement, SelectV2Props>((props, ref) => {
  const {
    labelProps,
    alerts,
    id,
    items,
    testID = "select",
    ...otherProps
  } = props;

  const fallbackID = useId();

  return (
    <ReactAriaProviders
      fieldID={id || fallbackID}
      externallyLabelledBy={props["aria-labelledby"]}
      externallyDescribedBy={props["aria-describedby"]}
      labelProps={labelProps}
      alerts={alerts}
    >
      {({ labelledBy, describedBy }) => (
        <InputWrapper alert={alerts && <InputAlerts alerts={alerts} />}>
          <InputLabelTopV2 {...labelProps}>
            <SelectPrimitive
              {...otherProps}
              id={id || fallbackID}
              aria-labelledby={labelledBy}
              aria-describedby={describedBy}
              data-testid={testID}
            >
              <OuterField>
                <S.Button ref={ref}>
                  <SelectValue />
                  <ChevronIcon />
                </S.Button>
              </OuterField>

              <Popover>
                <S.ListBox>
                  <ListBoxSection items={items}>
                    {(item) => (
                      <ListBoxItem id={item.value} textValue={item.title}>
                        {item.title}
                      </ListBoxItem>
                    )}
                  </ListBoxSection>
                </S.ListBox>
              </Popover>
            </SelectPrimitive>
          </InputLabelTopV2>
        </InputWrapper>
      )}
    </ReactAriaProviders>
  );
});

SelectV2.displayName = "SelectV2";

export default SelectV2;
